// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';

// material ui
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Avatar,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// api
import { useGetEnergySavingsQuery } from '@/api/energy-analysis/calculateEnergySavingsApiSlice';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { formatNumber } from '@/utils/formatters';

import { DateTime } from 'luxon';

const getTrendColor = (percentage) => {
  if (percentage >= 0) {
    return 'success'; // Green for positive savings
  } else {
    return 'error'; // Red for negative savings
  }
};
const formatDateRange = (startISO, endISO) => {
  if (!startISO && !endISO) return '-';

  const formatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  if (startISO && !endISO) return `${formatter.format(new Date(startISO))} –`;
  if (!startISO && endISO) return `- ${formatter.format(new Date(endISO))}`;

  const start = new Date(startISO);
  const end = new Date(endISO);

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  const sameMonth =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();

  const sameYear = start.getFullYear() === end.getFullYear();

  const dayMonthFormatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
  });

  const fullFormatter = formatter;

  if (sameDay) return fullFormatter.format(start);
  if (sameMonth) return `${start.getDate()}.–${fullFormatter.format(end)}`;
  if (sameYear)
    return `${dayMonthFormatter.format(start)} – ${fullFormatter.format(end)}`;
  return `${fullFormatter.format(start)} – ${fullFormatter.format(end)}`;
};
const EsgInitiativesDatagrid = ({ selectedEnergyGroup, timerangeMS }) => {
  const localeText = useDatagridLocaleText();

  const { t } = useTranslation('energyxplorer-initiatives-tab');
  const {
    data: energySavingsData,
    isLoading: isLoadingEnergySavingsData,
    isFetching: isFetchingEnergySavingsData,
  } = useGetEnergySavingsQuery(
    {
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      accountId: selectedEnergyGroup?.accountId,
    },
    {
      skip: !selectedEnergyGroup?.energyGroupId,
    },
  );

  const enrichedInitiatives = useMemo(() => {
    const initiatives = selectedEnergyGroup?.energyInitiatives || [];
    const savings = energySavingsData?.InitiativeEnergySavings || [];

    // Create a lookup map from InitiativeEnergySavings
    const savingsMap = new Map(savings.map((s) => [s.Id, s]));

    // Merge matching savings data into each initiative
    return initiatives.map((initiative) => {
      const savingsData = savingsMap.get(initiative.energyInitiativeId);
      return {
        ...initiative,
        energySavings: savingsData || null,
      };
    });
  }, [selectedEnergyGroup, energySavingsData?.InitiativeEnergySavings]);

  const rows = useMemo(() => {
    if (isLoadingEnergySavingsData || isFetchingEnergySavingsData) {
      return [];
    }

    const initiatives = enrichedInitiatives || [];
    const archivedInitiativesEndingInPeriod = initiatives.filter(
      (initiative) => {
        const isDateWithinRange = (dateStr, range) => {
          if (!dateStr || !range?.start || !range?.end) return false;
          const date = DateTime.fromISO(dateStr);
          const start = DateTime.fromMillis(range.start);
          const end = DateTime.fromMillis(range.end);
          return date >= start && date <= end;
        };
        return (
          initiative.state === 'Archived' &&
          isDateWithinRange(initiative.comparisonEnd, timerangeMS)
        );
      },
    );

    const baseRows = archivedInitiativesEndingInPeriod.map((initiative) => ({
      id: Number(initiative.energyInitiativeId),
      name: initiative.name,
      description: initiative.description,
      baselineStart: initiative.baselineStart,
      baselineEnd: initiative.baselineEnd,
      comparisonStart: initiative.comparisonStart,
      comparisonEnd: initiative.comparisonEnd,
      energyTreeNodes: initiative.energyTreeNodes,
      state: initiative.state,
      createdDate: initiative.createdDate,
      changedDate: initiative.changedDate,
      completedDate: initiative.completedDate,
      data: initiative.energySavings,
      advancedSavingsAvailable: initiative.energySavings?.AdvancedModelUsed,
      estimatedSavingsYearlyPercentage: initiative.energySavings
        ?.AdvancedModelUsed
        ? initiative.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
            ?.EnergySavingsPercentage
        : initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
            ?.EnergySavingsPercentage,
      estimatedSavingsYearly: initiative.energySavings?.AdvancedModelUsed
        ? initiative.energySavings?.ForecastedSavings?.AdvEstimatedYearlySavings
        : initiative.energySavings?.ForecastedSavings
            ?.BasicEstimatedYearlySavings,
      estimatedSavingsCo2eYearlyPercentage: initiative.energySavings
        ?.AdvancedModelUsed
        ? initiative.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
            ?.CO2eSavingsPercentage
        : initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
            ?.CO2eSavingsPercentage,
      estimatedSavingsCo2eYearly: initiative.energySavings?.AdvancedModelUsed
        ? initiative.energySavings?.ForecastedSavings
            ?.AdvEstimatedYearlySavingsCO2e
        : initiative.energySavings?.ForecastedSavings
            ?.BasicEstimatedYearlySavingsCO2e,
      main: initiative,
    }));

    return baseRows;
  }, [
    isLoadingEnergySavingsData,
    isFetchingEnergySavingsData,
    enrichedInitiatives,
    timerangeMS,
  ]);

  const columns = [
    {
      field: 'name',
      headerName: t('columns.name_esg.header'),
      description: t('columns.name_esg.description'),
      flex: 2,
      sortable: false, // disables sorting for this column
      filterable: false,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: params.row.advancedSavingsAvailable
                  ? alpha('#c2dd00', 0.1)
                  : 'transparent',
                mr: 1,
                borderRadius: 3,
              }}
            >
              <ModelTrainingRoundedIcon
                sx={(theme) => ({
                  color: params.row.advancedSavingsAvailable
                    ? '#96b100'
                    : theme.palette.mode === 'light'
                      ? 'lightgray'
                      : 'gray',
                })}
              />
            </Avatar>
            <Typography>{params.value}</Typography>
          </Box>
        );
      },
    },

    {
      field: 'baselinePeriod',
      headerName: t('columns.duration.header'),
      description: t('columns.duration.description'),
      flex: 3,
      sortable: false, // disables sorting for this column
      filterable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          justifyContent={'center'}
        >
          <Box display="flex" gap={1}>
            <Typography variant="caption" color="textSecondary">
              {t('common.baseline_period')}:{' '}
            </Typography>
            <Typography variant="caption">
              {formatDateRange(
                params.row.baselineStart,
                params.row.baselineEnd,
              )}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Typography variant="caption" color="textSecondary">
              {t('common.comparison_period')}:{' '}
            </Typography>
            <Typography variant="caption">
              {formatDateRange(
                params.row.comparisonStart,
                params.row.comparisonEnd,
              )}
            </Typography>
          </Box>
        </Box>
      ),
    },

    {
      field: 'estimatedSavingsYearly',
      headerName: t('columns.estimated_savings_yearly.header'),
      description: t('columns.estimated_savings_yearly.description'),
      flex: 2,
      minWidth: 180,
      renderCell: (params) => {
        const value = params?.value;
        const formattedValue = value ? formatNumber(value) : '-';
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {value ? (
              <>
                <Typography minWidth={130}>
                  {t('datagrid.formats.kwh', { value: formattedValue })}
                </Typography>
                <Chip
                  variant="filled"
                  sx={{
                    marginLeft: 1,
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      alpha(
                        theme.palette[
                          getTrendColor(
                            params.row.estimatedSavingsYearlyPercentage,
                          )
                        ].main,
                        0.1,
                      ),
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette[
                            getTrendColor(
                              params.row.estimatedSavingsYearlyPercentage,
                            )
                          ].dark
                        : theme.palette[
                            getTrendColor(
                              params.row.estimatedSavingsYearlyPercentage,
                            )
                          ].light,
                  }}
                  label={
                    params.row.estimatedSavingsYearlyPercentage
                      ? `${params.row.estimatedSavingsYearlyPercentage.toFixed(1)}%`
                      : '0%'
                  }
                  size="small"
                />{' '}
              </>
            ) : (
              '-'
            )}
          </Box>
        );
      },
    },

    {
      field: 'estimatedSavingsCo2eYearly',
      headerName: t('columns.estimated_co2e_savings_yearly.header'),
      description: t('columns.estimated_co2e_savings_yearly.description'),

      flex: 2,
      minWidth: 180,
      renderCell: (params) => {
        const value = params?.value / 1000;
        const formattedValue = value ? formatNumber(value) : '-';
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {value ? (
              <>
                <Typography minWidth={130}>
                  {t('datagrid.formats.co2e', { value: formattedValue })}
                </Typography>
                <Chip
                  variant="filled"
                  sx={{
                    marginLeft: 1,
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      alpha(
                        theme.palette[
                          getTrendColor(
                            params.row.estimatedSavingsCo2eYearlyPercentage,
                          )
                        ].main,
                        0.1,
                      ),
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette[
                            getTrendColor(
                              params.row.estimatedSavingsCo2eYearlyPercentage,
                            )
                          ].dark
                        : theme.palette[
                            getTrendColor(
                              params.row.estimatedSavingsCo2eYearlyPercentage,
                            )
                          ].light,
                  }}
                  label={
                    params.row.estimatedSavingsCo2eYearlyPercentage
                      ? `${params.row.estimatedSavingsCo2eYearlyPercentage.toFixed(1)}%`
                      : '0%'
                  }
                  size="small"
                />{' '}
              </>
            ) : (
              '-'
            )}
          </Box>
        );
      },
    },
  ];
  const isLoading = isLoadingEnergySavingsData || isFetchingEnergySavingsData;
  const hasRows = rows.length > 0;
  const showEmptyState = !hasRows && !isLoading;
  const countLabel = isLoading ? '…' : rows.length;

  return (
    <Accordion
      defaultExpanded
      variant="outlined"
      sx={{
        mb: 0,
        borderRadius: '8px !important',
        boxShadow: '0px 5px 10px -3px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        '&:before': { display: 'none' },
        '&:first-of-type': {
          borderTopLeftRadius: '8px !important',
          borderTopRightRadius: '8px !important',
        },
        '&:last-of-type': {
          borderBottomLeftRadius: '8px !important',
          borderBottomRightRadius: '8px !important',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        sx={{ minHeight: 'auto', '&.Mui-expanded': { minHeight: 'auto' } }}
      >
        <Box display="flex" alignItems="center" gap={1} width="100%">
          <Typography variant="subtitle1">
            {t('columns.name_esg.header')}
          </Typography>
          <Chip size="small" label={countLabel} />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {showEmptyState ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('no_archived_initiatives_in_period')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <DataGridPremium
              rows={rows}
              columns={columns}
              hideFooter
              loading={isLoading}
              localeText={localeText}
            />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default EsgInitiativesDatagrid;
